
// client/src/components/media/Calendar.jsx
// Calendar component using Dashboard-style API calls (axios, useParams, same endpoints).
// - Month view: 31 days starting from cursor date, event count per day, click to see details
// - Week view: 7 days starting from cursor date, event count per day, click to see details
// - Day view: active events (linked), active auctions/raffles (type-prefixed, linked), up to 10 scheduled posts

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import AppFooter from '../AppFooter';
import { sessionRequestConfig } from '../../services/sessionLocal';
import {
  addDays,
  eachDayOfInterval,
  format,
  isSameDay,
  parseISO,
  startOfDay,
} from "date-fns";
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, Clock, MapPin, List, Plus } from "lucide-react";

// ===== Helpers =====

const dayKey = (d) => format(d, "yyyy-MM-dd");

/**
 * Check if an event is a "Timeline" event (excluded from display, same as Dashboard).
 */
const isTimelineEvent = (event) => {
  const name = String(event?.name ?? event?.title ?? '').trim().toLowerCase();
  const type = String(event?.type ?? event?.eventtype ?? '').trim().toLowerCase();
  return name === 'timeline' || type === 'timeline';
};

/**
 * Parse a date value safely.
 * Handles MySQL datetime ("YYYY-MM-DD HH:MM:SS"), ISO strings, and Date objects.
 */
function parseDateSafe(value) {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  const raw = String(value);
  // Handle MySQL "YYYY-MM-DD HH:MM:SS" format by converting to ISO
  const normalized =
    /\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(raw) && !raw.includes("T")
      ? raw.replace(" ", "T")
      : raw;
  try {
    const d = parseISO(normalized);
    return isNaN(d.getTime()) ? null : d;
  } catch {
    const d = new Date(raw);
    return isNaN(d.getTime()) ? null : d;
  }
}

/**
 * Check if an event from t_event is active on a given day.
 * An event spans from startdate to enddate; the day is "active" if within that range.
 * Matches Dashboard getEventsForDay logic (filters out Timeline events and invalid dates).
 */
function isEventActiveOnDay(event, day) {
  if (isTimelineEvent(event)) return false;
  if (!event?.startdate || !event?.enddate) return false;
  const start = parseDateSafe(event.startdate);
  const end = parseDateSafe(event.enddate);
  if (!start || !end) return false;
  return day >= startOfDay(start) && day <= end;
}

/**
 * Check if an auction/raffle from scheduled-events API is active on a given day.
 * Uses startDate/start_at and endDate/end_at fields.
 */
function isScheduledEventActiveOnDay(item, day) {
  const startRaw = item.startDate ?? item.start_at;
  const endRaw = item.endDate ?? item.end_at;
  const start = parseDateSafe(startRaw);
  const end = parseDateSafe(endRaw);
  if (!start || !end) return false;
  return day >= startOfDay(start) && day <= end;
}

/**
 * Check if a scheduled post is set to publish on a specific day.
 * Compares only the date portion (ignores time).
 */
function isPostScheduledForDay(post, day) {
  const scheduledRaw = post.scheduled_on ?? post.scheduledOn ?? post.scheduledAt;
  const scheduled = parseDateSafe(scheduledRaw);
  if (!scheduled) return false;
  return isSameDay(scheduled, day);
}

/**
 * Safely parse the platforms field which may be a JSON string or already an array.
 */
function parsePlatforms(platforms) {
  if (Array.isArray(platforms)) return platforms;
  if (typeof platforms === 'string') {
    try { return JSON.parse(platforms); } catch { return []; }
  }
  return [];
}

// Small colored dot indicator
const Dot = ({ color }) => (
  <span
    className="inline-block h-1.5 w-1.5 rounded-full"
    style={{ backgroundColor: color || '#ff6800' }}
  />
);

// ===== Main Component =====
export default function Calendar({
  tenantId: tenantIdProp,
  initialView = "month",
  events: eventsProp,
  onEventClick,
}) {
  const navigate = useNavigate();
  // Use useParams to resolve tenant (same pattern as Dashboard)
  const { tenant: tenantFromRoute } = useParams();
  const baseUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:5000';

  // Resolve tenantId: prop > route param > localStorage fallback
  const resolvedTenantId = useMemo(() => {
    const id = tenantIdProp ?? tenantFromRoute ?? localStorage.getItem("currentTenantId") ?? "";
    return String(id);
  }, [tenantIdProp, tenantFromRoute]);

  const [view, setView] = useState(initialView); // "month" | "week" | "day"
  const [cursor, setCursor] = useState(startOfDay(new Date()));

  // Data state — matches Dashboard data patterns
  const [events, setEvents] = useState([]);             // Raw events from /api/events/:tenant/events
  const [scheduledEvents, setScheduledEvents] = useState([]); // Auctions/raffles from /api/events/:tenant/scheduled-events
  const [scheduledPosts, setScheduledPosts] = useState([]);   // Posts from /api/schedule/upcoming/:tenant
  const [tasks, setTasks] = useState([]);                     // Tasks from /api/events/:tenant/tasks
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Dialog state
  const [dayDialogOpen, setDayDialogOpen] = useState(false);
  const [dayDialogDate, setDayDialogDate] = useState(null);
  const [activeEvent, setActiveEvent] = useState(null);

  // Persist tenant ID to localStorage (same as original)
  useEffect(() => {
    if (resolvedTenantId) {
      try { localStorage.setItem("currentTenantId", String(resolvedTenantId)); } catch {}
    }
  }, [resolvedTenantId]);

  // ===== Data Fetching (Dashboard-style: axios, parallel requests) =====
  const loadData = useCallback(async () => {
    if (!resolvedTenantId) return;

    setLoading(true);
    setError(null);

    const shouldFetchEvents = !eventsProp || !eventsProp.length;

    try {
      // Fetch all data sources in parallel using Promise.allSettled for resilience.
      // If one endpoint fails the others still populate.
      const [eventResult, seResult, postsResult, taskResult] = await Promise.allSettled([
        // Events: use same endpoint as Dashboard GET /api/events/:tenant/events
        shouldFetchEvents
          ? axios.get(`${baseUrl}/api/events/${resolvedTenantId}/events`)
          : Promise.resolve(null),
        // Auctions & Raffles: same endpoint as ScheduledEventsList
        axios.get(`${baseUrl}/api/events/${resolvedTenantId}/scheduled-events`, {
          params: { limit: 100, offset: 0 },
        }),
        // Scheduled Posts: same endpoint as ScheduledPostsList
        axios.get(`${baseUrl}/api/schedule/upcoming/${resolvedTenantId}`, {
          params: { limit: 50, offset: 0 },
        }),
        // Tasks: tenant-wide list (past 12 months + all future)
        (() => {
          const cfg = sessionRequestConfig();
          return axios.get(`${baseUrl}/api/events/${resolvedTenantId}/tasks`, {
            ...cfg,
            params: { ...cfg.params, range: 'year' },
          });
        })(),
      ]);

      // Process events (response shape: { success: true, data: [...] })
      if (shouldFetchEvents) {
        if (eventResult.status === 'fulfilled' && eventResult.value?.data?.success) {
          setEvents(eventResult.value.data.data || []);
        } else if (eventResult.status === 'rejected') {
          console.error('[Calendar] Failed to load events:', eventResult.reason);
        }
      } else {
        // Use events passed via props
        setEvents(eventsProp);
      }

      // Process auctions/raffles (response shape: { success: true, events: [...] })
      if (seResult.status === 'fulfilled') {
        const seData = seResult.value?.data;
        setScheduledEvents(Array.isArray(seData?.events) ? seData.events : []);
      } else {
        console.error('[Calendar] Failed to load scheduled events:', seResult.reason);
      }

      // Process scheduled posts (response shape: { success: true, posts: [...] })
      if (postsResult.status === 'fulfilled') {
        const pData = postsResult.value?.data;
        setScheduledPosts(Array.isArray(pData?.posts) ? pData.posts : []);
      } else {
        console.error('[Calendar] Failed to load scheduled posts:', postsResult.reason);
      }

      // Process tasks (response shape: { success: true, tasks: [...] })
      if (taskResult.status === 'fulfilled') {
        const tData = taskResult.value?.data;
        setTasks(Array.isArray(tData?.tasks) ? tData.tasks : []);
      } else {
        console.error('[Calendar] Failed to load tasks:', taskResult.reason);
      }
    } catch (err) {
      console.error('[Calendar] Error loading data:', err);
      setError(err?.message || 'Failed to load calendar data');
    } finally {
      setLoading(false);
    }
  }, [resolvedTenantId, eventsProp, baseUrl]);

  useEffect(() => { loadData(); }, [loadData]);

  // ===== Calendar day ranges =====

  // Month view: 31 consecutive days starting from cursor
  const monthDays = useMemo(() => {
    return eachDayOfInterval({
      start: startOfDay(cursor),
      end: addDays(startOfDay(cursor), 30), // indices 0..30 = 31 days
    });
  }, [cursor]);

  // Week view: 7 consecutive days starting from cursor.
  // If "today" falls inside the displayed range, hoist it to index 0 so the
  // feature card at the top of the week view (sized larger via desktop.css)
  // shows the current day. When the user navigates to a different week, the
  // cursor day becomes index 0 instead — keeping the navigational anchor
  // featured at the top.
  const weekDays = useMemo(() => {
    const base = eachDayOfInterval({
      start: startOfDay(cursor),
      end: addDays(startOfDay(cursor), 6),
    });
    const today = startOfDay(new Date());
    const todayIdx = base.findIndex((d) => isSameDay(d, today));
    if (todayIdx > 0) {
      return [base[todayIdx], ...base.slice(0, todayIdx), ...base.slice(todayIdx + 1)];
    }
    return base;
  }, [cursor]);

  // ===== Active events per day (for month/week event count badges) =====
  const eventsByDay = useMemo(() => {
    const map = {};
    const days = view === "month" ? monthDays : weekDays;
    days.forEach((d) => {
      const k = dayKey(d);
      // Filter events active on this day (same logic as Dashboard getEventsForDay)
      map[k] = events.filter((ev) => isEventActiveOnDay(ev, d));
    });
    return map;
  }, [events, monthDays, weekDays, view]);

  // ===== Navigation actions =====
  // Month shifts by 31 days, week by 7 days, day by 1 day
  const prev = () =>
    setCursor((v) => addDays(v, view === "month" ? -31 : view === "week" ? -7 : -1));
  const next = () =>
    setCursor((v) => addDays(v, view === "month" ? 31 : view === "week" ? 7 : 1));
  const today = () => setCursor(startOfDay(new Date()));
  const openDay = (d) => { setDayDialogDate(d); setDayDialogOpen(true); };

  // ===== Navigation paths (matches TenantRoutes patterns) =====
  const eventDetailPath = (eventId) =>
    resolvedTenantId && eventId ? `/${resolvedTenantId}/${eventId}/eventDetail` : '#';

  const auctionConfigPath = (eventId, eventItemId) =>
    resolvedTenantId && eventId && eventItemId
      ? `/${resolvedTenantId}/${eventId}/${eventItemId}/auctionConfig`
      : '#';

  const raffleConfigPath = (eventId, eventItemId) =>
    resolvedTenantId && eventId && eventItemId
      ? `/${resolvedTenantId}/${eventId}/${eventItemId}/raffleConfig`
      : '#';

  // ===== Per-day data accessors (used by day view and modal) =====
  const getEventsForDay = useCallback(
    (day) => events.filter((ev) => isEventActiveOnDay(ev, day)),
    [events]
  );

  const getScheduledEventsForDay = useCallback(
    (day) => scheduledEvents.filter((item) => isScheduledEventActiveOnDay(item, day)),
    [scheduledEvents]
  );

  const getPostsForDay = useCallback(
    (day) => scheduledPosts.filter((post) => isPostScheduledForDay(post, day)),
    [scheduledPosts]
  );

  const getTasksForDay = useCallback(
    (day) => tasks.filter((task) => {
      const raw = task.duedatetime || task.taskdate || task.date;
      const d = parseDateSafe(raw);
      return d && isSameDay(d, day);
    }),
    [tasks]
  );

  // ===== Header date label =====
  const headerLabel = useMemo(() => {
    if (view === "day") return format(cursor, "PPPP");
    const endDate = addDays(cursor, view === "month" ? 30 : 6);
    return `${format(cursor, "MMM d, yyyy")} – ${format(endDate, "MMM d, yyyy")}`;
  }, [cursor, view]);

  // ===== Day Cell for month/week grids =====
  // Shows day name, date, active event count, and up to 3 event name previews.
  // Clicking the cell opens a detail modal.
  const DayCell = ({ d }) => {
    const k = dayKey(d);
    const list = eventsByDay[k] || [];
    const isToday = isSameDay(d, new Date());

    return (
      <div
        className={`border p-2 rounded ${isToday ? "border-primary bg-light" : "bg-white"}`}
        style={{ height: 110, cursor: 'pointer', overflow: 'hidden' }}
        onClick={() => openDay(d)}
      >
        {/* Day header: day-of-week + date + event count */}
        <div className="flex w-full items-center justify-between" style={{ minWidth: 0 }}>
          <span className={`text-sm font-semibold ${isToday ? "text-primary" : ""}`} style={{ whiteSpace: 'nowrap' }}>
            {format(d, "EEE")} <strong>{format(d, "d")}</strong>
          </span>
          <span className="small text-gray-400" style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
            {list.length} {list.length === 1 ? 'event' : 'events'}
          </span>
        </div>
        {/* Event name previews (up to 3) */}
        <div className="mt-1 flex flex-col" style={{ gap: 2, overflow: 'hidden', minWidth: 0 }}>
          {list.slice(0, 3).map((ev) => (
            <div key={`${k}-${ev.id}`} className="flex items-center" style={{ gap: 4, minWidth: 0 }}>
              <Dot color={ev.color} />
              <span className="small" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, flex: 1 }}>{ev.name || ev.title}</span>
            </div>
          ))}
          {list.length > 3 && (
            <span className="small text-gray-400">+{list.length - 3} more</span>
          )}
        </div>
      </div>
    );
  };

  // ===== Pre-compute day view data =====
  const dayEvents = useMemo(() => getEventsForDay(cursor), [cursor, getEventsForDay]);
  const dayScheduledEvents = useMemo(
    () => getScheduledEventsForDay(cursor),
    [cursor, getScheduledEventsForDay]
  );
  // Day view shows up to 10 scheduled posts for the current day
  const dayPosts = useMemo(
    () => getPostsForDay(cursor).slice(0, 10),
    [cursor, getPostsForDay]
  );
  const dayTasks = useMemo(() => getTasksForDay(cursor), [cursor, getTasksForDay]);

  // ===== Render =====
  return (
    <div className="container py-3" style={{ paddingBottom: 72 }}>
      {/* Header */}
      <div className="flex items-center gap-2 mb-2">
        <button className="btn btn-outline-secondary" onClick={prev}><ChevronLeft size={16} /></button>
        <button className="btn btn-outline-secondary" onClick={next}><ChevronRight size={16} /></button>
        <button className="btn btn-secondary" onClick={today}>Today</button>
      </div>
      <div className="flex items-center gap-2 mb-2 text-lg font-semibold">
        <CalendarIcon size={16} /> {headerLabel}
      </div>
      <div className="mb-3 flex gap-0 border-b border-gray-600">
        {["month", "week", "day"].map((v) => (
          <button
            key={v}
            className={`calendar-view-tab px-4 py-2 text-sm font-medium capitalize border-b-2 bg-transparent cursor-pointer ${
              view === v
                ? "active border-cobalt text-white"
                : "border-transparent text-gray-400 hover:text-white"
            }`}
            onClick={() => setView(v)}
          >
            {v}
          </button>
        ))}
      </div>

      {/* Body */}
      {loading && <div className="border rounded p-3 small text-gray-400">Loading events…</div>}
      {error && <div className="border rounded p-3 small text-red-600">{error}</div>}

      {!loading && !error && (
        <div className="border rounded p-3 bg-white">

          {/* ===== Month View: 31 days starting with current date =====
              Three columns per row so each cell has enough width for the
              day label and event list to be legible. Was row-cols-7 which
              squeezed text to unreadable widths on mobile. */}
          {view === "month" && (
            <div className="row row-cols-3 gap-2">
              {monthDays.map((d) => (
                <div key={dayKey(d)} className="col"><DayCell d={d} /></div>
              ))}
            </div>
          )}

          {/* ===== Week View: 7 days starting with current date =====
              Three columns per row (like Month view) so each cell has
              enough width for the day label and event list to be
              legible. Was row-cols-7, which squeezed all seven days
              into one row and made the text unreadable on mobile. */}
          {view === "week" && (
            <div className="row row-cols-3 gap-2">
              {weekDays.map((d) => (
                <div key={dayKey(d)} className="col"><DayCell d={d} /></div>
              ))}
            </div>
          )}

          {/* ===== Day View: events + auctions/raffles + scheduled posts ===== */}
          {view === "day" && (
            <div>
              <div className="px-3 py-2 small text-gray-400">{format(cursor, "PPPP")}</div>

              {/* --- Active Events Section --- */}
              <div className="p-2">
                <h6 className="text-gray-400 mb-2">Events</h6>
                {dayEvents.length > 0 ? (
                  dayEvents.map((ev) => (
                    <div key={`event-${ev.id}`} className="border-b py-2 px-3">
                      {/* Event name as a navigable link to EventDetail */}
                      <a
                        href={eventDetailPath(ev.id)}
                        onClick={(e) => {
                          // Allow cmd/ctrl-click to open in new tab
                          if (e.metaKey || e.ctrlKey || e.shiftKey) return;
                          e.preventDefault();
                          if (onEventClick) return onEventClick(ev);
                          navigate(eventDetailPath(ev.id));
                        }}
                        className="font-medium link-primary underline"
                      >
                        {ev.name || ev.title}
                      </a>
                      {/* Event description */}
                      {(ev.description || ev.details) && (
                        <p className="small text-gray-400 mb-0 mt-1">
                          {ev.description || ev.details}
                        </p>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="px-3 py-2 small text-gray-400">No events for this day.</div>
                )}
              </div>

              {/* --- Active Auctions & Raffles Section --- */}
              {/* Shows type (auction/raffle) prepended to name, linked to respective config */}
              <div className="p-2 mt-2">
                <h6 className="text-gray-400 mb-2">Auctions &amp; Raffles</h6>
                {dayScheduledEvents.length > 0 ? (
                  dayScheduledEvents.map((item) => {
                    const typeLower = String(item.type || '').toLowerCase();
                    // Prepend type label to the name
                    const label = typeLower === 'auction'
                      ? `Auction: ${item.name || 'Unnamed'}`
                      : `Raffle: ${item.name || 'Unnamed'}`;
                    // Navigate to the correct config component based on type
                    const path = typeLower === 'auction'
                      ? auctionConfigPath(item.eventId, item.eventItemId)
                      : raffleConfigPath(item.eventId, item.eventItemId);

                    return (
                      <div key={`${item.type}-${item.id}`} className="border-b py-2 px-3 flex items-center justify-between">
                        <a
                          href={path}
                          onClick={(e) => {
                            if (e.metaKey || e.ctrlKey || e.shiftKey) return;
                            e.preventDefault();
                            navigate(path);
                          }}
                          className="font-medium link-primary underline"
                        >
                          {label}
                        </a>
                        <span
                          className={`badge ml-2 uppercase ${
                            typeLower === 'auction' ? 'bg-warning text-dark' : 'bg-info'
                          }`}
                        >
                          {item.status}
                        </span>
                      </div>
                    );
                  })
                ) : (
                  <div className="px-3 py-2 small text-gray-400">No active auctions or raffles.</div>
                )}
              </div>

              {/* --- Scheduled Posts Section (up to 10 for the day) --- */}
              <div className="p-2 mt-2">
                <h6 className="text-gray-400 mb-2">Scheduled Posts (up to 10)</h6>
                {dayPosts.length > 0 ? (
                  dayPosts.map((post) => {
                    const platforms = parsePlatforms(post.platforms);
                    const platformStr = platforms.join(', ');
                    const scheduledTime = parseDateSafe(post.scheduled_on);
                    // Truncate content for readability
                    const preview = post.content
                      ? (post.content.length > 80 ? post.content.slice(0, 80) + '…' : post.content)
                      : 'No content';

                    return (
                      <div key={`post-${post.id}`} className="border-b py-2 px-3">
                        <div className="small font-medium">{preview}</div>
                        <div className="small text-gray-400 flex items-center gap-2 mt-1">
                          <Clock size={12} />
                          <span>{scheduledTime ? format(scheduledTime, "p") : '—'}</span>
                          {platformStr && (
                            <span className="badge bg-gray-600 uppercase">{platformStr}</span>
                          )}
                          <span
                            className={`badge uppercase ${
                              post.status === 'queued' ? 'bg-warning text-dark' : 'bg-info'
                            }`}
                          >
                            {post.status}
                          </span>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="px-3 py-2 small text-gray-400">No scheduled posts for this day.</div>
                )}
              </div>

              {/* --- Tasks Section --- */}
              <div className="p-2 mt-2">
                <h6 className="text-gray-400 mb-2">Tasks</h6>
                {dayTasks.length > 0 ? (
                  dayTasks.map((task) => {
                    const due = parseDateSafe(task.duedatetime);
                    const path = resolvedTenantId && task.eventid
                      ? `/${resolvedTenantId}/${task.eventid}/task`
                      : '#';
                    return (
                      <div key={`task-${task.id}`} className="border-b py-2 px-3 flex items-center justify-between">
                        <a
                          href={path}
                          onClick={(e) => {
                            if (e.metaKey || e.ctrlKey || e.shiftKey) return;
                            e.preventDefault();
                            navigate(path);
                          }}
                          className="font-medium link-primary underline"
                          style={{ textDecoration: task.status === 'completed' ? 'line-through' : undefined }}
                        >
                          {task.title || task.name}
                          {task.eventName ? ` · ${task.eventName}` : ''}
                        </a>
                        <span className="small text-gray-400 flex items-center gap-2" style={{ flexShrink: 0 }}>
                          {due && <><Clock size={12} /> {format(due, 'p')}</>}
                          <span className="badge uppercase bg-gray-600">{task.priority || 'medium'}</span>
                        </span>
                      </div>
                    );
                  })
                ) : (
                  <div className="px-3 py-2 small text-gray-400">No tasks for this day.</div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Day list dialog — opens when clicking a day card in month/week view. */}
      {dayDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setDayDialogOpen(false)} />
          <div className="relative w-full max-w-2xl mx-4 rounded-lg bg-dark-bg border border-gray-700">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700">
              <h5 className="font-semibold m-0">{dayDialogDate ? format(dayDialogDate, "PPPP") : "Day"}</h5>
              <button className="btn btn-close btn-close-white" onClick={() => setDayDialogOpen(false)} />
            </div>
            <div className="px-4 py-3" style={{ maxHeight: "60vh", overflowY: "auto" }}>
              {dayDialogDate && (() => {
                const dialogEvents = getEventsForDay(dayDialogDate);
                const dialogTasks = getTasksForDay(dayDialogDate);
                if (dialogEvents.length === 0 && dialogTasks.length === 0) {
                  return <div className="p-3 small text-gray-400">Nothing scheduled for this day.</div>;
                }
                return (
                  <>
                    {dialogEvents.map((ev) => (
                      <div
                        key={`dlg-event-${ev.id}`}
                        className="flex items-center justify-between gap-3 py-2 px-3 border-b"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <Dot color={ev.color} />
                          <span className="font-medium truncate">{ev.name || ev.title}</span>
                        </div>
                        <button
                          type="button"
                          className="btn btn-outline-primary btn-sm"
                          style={{ width: "fit-content", padding: "0.25rem 5px", whiteSpace: "nowrap", flexShrink: 0 }}
                          onClick={() => {
                            setDayDialogOpen(false);
                            navigate(eventDetailPath(ev.id));
                          }}
                        >
                          View Event
                        </button>
                      </div>
                    ))}
                    {dialogTasks.map((task) => (
                      <div
                        key={`dlg-task-${task.id}`}
                        className="flex items-center justify-between gap-3 py-2 px-3 border-b"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <Dot color="#6366f1" />
                          <span
                            className="truncate"
                            style={{ textDecoration: task.status === 'completed' ? 'line-through' : undefined }}
                          >
                            {task.title || task.name}
                            {task.eventName ? ` · ${task.eventName}` : ''}
                          </span>
                        </div>
                        {task.eventid && (
                          <button
                            type="button"
                            className="btn btn-outline-primary btn-sm"
                            style={{ width: "fit-content", padding: "0.25rem 5px", whiteSpace: "nowrap", flexShrink: 0 }}
                            onClick={() => {
                              setDayDialogOpen(false);
                              navigate(`/${resolvedTenantId}/${task.eventid}/task`);
                            }}
                          >
                            View Task
                          </button>
                        )}
                      </div>
                    ))}
                  </>
                );
              })()}
            </div>
            <div className="flex justify-end px-4 py-3 border-t border-gray-700">
              <button className="btn btn-secondary" onClick={() => setDayDialogOpen(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Event quick-view modal */}
      {activeEvent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setActiveEvent(null)} />
          <div className="relative w-full max-w-2xl mx-4 rounded-lg bg-dark-bg border border-gray-700">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700">
              <h5 className="font-semibold m-0">{activeEvent.name || activeEvent.title}</h5>
              <button className="btn btn-close btn-close-white" onClick={() => setActiveEvent(null)} />
            </div>
            <div className="px-4 py-3">
              <div className="flex items-center gap-2 small mb-2">
                <Clock size={16} />
                <span>
                  {parseDateSafe(activeEvent.startdate)
                    ? format(parseDateSafe(activeEvent.startdate), "PPPP p")
                    : '—'}
                  {' – '}
                  {parseDateSafe(activeEvent.enddate)
                    ? format(parseDateSafe(activeEvent.enddate), "p")
                    : '—'}
                </span>
              </div>
              {activeEvent.location && (
                <div className="flex items-center gap-2 small mb-2">
                  <MapPin size={16} />
                  <span>{activeEvent.location}</span>
                </div>
              )}
              {(activeEvent.description || activeEvent.details) && (
                <p className="mb-0">{activeEvent.description || activeEvent.details}</p>
              )}
            </div>
            <div className="flex justify-end gap-2 px-4 py-3 border-t border-gray-700">
              <button
                className="btn btn-primary"
                onClick={() => navigate(eventDetailPath(activeEvent.id))}
              >
                Open Event
              </button>
              <button className="btn btn-secondary" onClick={() => setActiveEvent(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="mt-3 small text-gray-400">
        Tenant: <span className="font-monospace">{resolvedTenantId || "(none)"}</span>
      </div>

      <AppFooter
        buttons={[
          {
            icon: <List size={16} />,
            label: 'Event List',
            onClick: () => navigate(`/${resolvedTenantId}/events`),
          },
          {
            icon: <Plus size={16} />,
            label: 'Add Event',
            onClick: () => navigate(`/${resolvedTenantId}/eventAdd`),
          },
        ]}
      />
    </div>
  );
}
